import AmpEvents from '@amplify/amp-events';
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import omit from 'lodash.omit';
import { getUrlParams } from '../../../utils/shared/urlUtils';
import {
    GENERATE_PDF,
    SELECT_STUDENT,
    SWITCH_INSTRUCTION_DATE,
    SWITCH_PERIOD,
    VIEW_LATEST_UPDATE_RECOMMENDATION,
    SELECT_GRADE_YEAR,
} from '../../../api/actionTypes/shared/apiActionTypes';
import { INSTRUCTION as INSTRUCTION_INFO } from '../../../constants/shared/CommonConstants';
import {
    ALL_PM_GRAPHS_PDF,
    ALL_STUDENT_SUMMARY_PDF,
    CLASS_SUMMARY_PDF,
    CURRENT_PAGE,
    HOME_CONNECT_PDF,
    TRO_CLASS_SUMMARY_PDF,
} from '../../../constants/shared/PdfGenerationConstants';
import {
    LRS_CONTEXT_EXTENSIONS,
    LRS_RESULT_EXTENSIONS,
    LRS_NAVIGATION_TAB,
    LRS_PAGE,
    LRS_XAPI_LOCATION_OBJECT_IDS,
    LRS_EVENTS_XAPI_IDS,
    LRS_VERBS,
    LRS_XAPI_ONCLICK_OBJECT_IDS,
    STUDENT_SUMMARY_TABS,
} from '../../../constants/shared/LrsConstants';
import LrsPageMapper from './LrsPageMapper';
import { ENV, isEnvPresent } from '../../../constants/shared/envConstants';
import LOOKER_ACTIONS from './lrsActionsConstant';
import { getDetailedMeasureName, getMeasureTitleByKey } from '../../../utils/shared/measureUtils';
import { SORT_DIRECTIONS } from '../../../constants/shared/DualLanguageConstants';
import { convertToLowerKebabCase } from '../../../utils/shared/commonUtils';
import parseGradeYearPair from '../../../api/studentSummary/shared/studentSummaryParsers';

const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const { GROUPS, STUDENTS, ALL_ACTIVITIES } = INSTRUCTION_INFO;
const {
    BENCHMARK_PAGE,
    PROGRESS_PAGE,
    HOME_CONNECT_PAGE,
    INSTRUCTIONS_PAGE,
    DUAL_LANGUAGE_PAGE,
    STUDENT_SUMMARY_BENCHMARK_HISTORY_PAGE,
    STUDENT_SUMMARY_TEXT_READING_HISTORY_PAGE,
    STUDENT_SUMMARY_PROGRESS_PAGE,
} = LRS_PAGE;
const { INSTRUCTION } = LRS_NAVIGATION_TAB;
const {
    PROBE_DETAIL_VISITING_EVENT,
    PM_GRAPH_ZOOM_EVENT,
    PERCENTILE_EVENT,
    SELECT_INDIVIDUAL_STUDENT,
    DOWNLOAD_STUDENT_ACTIVITY,
    SORT_STUDENTS_BY_NAME,
    HELP_DETAILS,
    DOWNLOAD_ALL_ACTIVITIES,
    SEE_ACTIVITIES,
    OBSERVATION_SELECT,
    SELECT_PAGE,
    SELECT_SKILL,
    DOWNLOAD_ACTIVITY,
    SWITCH_INSTRUCTION_TAB,
    SELECT_HELP_ITEM,
    PRINT_CURRENT_PAGE,
    DOWNLOAD_PDF_LANGUAGE_CONSIDERATION,
    PM_GRAPH_RESET_ZOOM_EVENT,
    SWITCH_PROGRESS_TAB,
    PROGRESS_GOAL_SET_EVENT,
    LEXILE_EVENT,
    SWITCH_LANGUAGE_CONSIDERATIONS_TAB,
    SELECTED_HEADER_DROPDOWN,
    SWITCH_PM_PERIOD,
    SWITCH_GROWTH_ANALYSIS_PERIOD,
} = LOOKER_ACTIONS;
const {
    TIME_OF_YEAR,
    RECOMMENDED_ACTIVITY,
    NAME_SORT,
    YEAR,
    GRADE,
    PROBE_DETAILS,
    PROBE_LOCATION,
    ZOOM_DETAILS,
    BUTTON_STATUS,
    RECOMMENDATION,
    PAST_RECOMMENDATION_TIME,
    RECOMMENDATION_TIME,
    ETERNAL_STUDENT_ID,
    DOWNLOAD_LOCATION,
    PREVIOUS_SEE_ACTIVITIES,
    PAGINATION_SELECTED,
    PARENT_GRADE,
    PARENT_SKILL,
    HELP_ITEM,
    PRINT_ITEM,
    TIME_FRAME,
    BUTTON_TEXT,
    LANGUAGE_CONSIDERATIONS,
    DISTRICT_ID,
    SCHOOL_ID,
    CLASS_ID,
    GROUP_ID,
} = LRS_CONTEXT_EXTENSIONS;
const {
    INSTRUCTIONAL_ACTIVITY_ID,
    PATTERN_OBSERVED,
    PATTERN_CATEGORY,
    PATTERN_ASSESSMENT,
    PATTERN_ENABLED,
    SEE_ACTIVITIES: SEE_ACTIVITIES_EXT,
    STUDENT_ENGLISH_ID,
    STUDENT_SPANISH_ID,
    PAGINATION_SHOWN,
    SKILL,
    PREVIOUS_GOAL_RANGE,
    PREVIOUS_GOAL_LEVEL,
    GOAL_SCORE,
    PREVIOUS_GOAL_SCORE,
    NAME_SHOWN,
    NAME_LIST,
    NAME_SELECTED,
} = LRS_RESULT_EXTENSIONS;
const { ACCESSED, SELECTED, DOWNLOADED, VIEWED, PRESSED, DEFINED } = LRS_VERBS;
const { EVENT_PRINT_ITEM, EVENT_DOWNLOAD_PDF_LANGUAGE_CONSIDERATION, EVENT_HELP_ITEM } =
    LRS_XAPI_ONCLICK_OBJECT_IDS;

export const getEnvironmentName = () => {
    if (isEnvPresent()) {
        switch (window.ENV) {
            case ENV.STAGING_ENV:
                return AmpEvents.ENVIRONMENTS.STAGING;

            case ENV.PRODUCTION_ENV:
                return AmpEvents.ENVIRONMENTS.PRODUCTION;

            default:
                return AmpEvents.ENVIRONMENTS.DEVCI;
        }
    }
    return null;
};

export const getLRSObjectForLocation = (location, withoutTabName) => {
    if (!location) {
        // wait until location is properly received
        return null;
    }

    const appRootUrl = `/${location.pathname.split('/')[1]}`;
    const mappedPageObject = LrsPageMapper(appRootUrl)[location.pathname];
    const queryParams = getUrlParams(location);

    if (isEmpty(queryParams)) {
        // URL update not ready, need to wait until the full URL is properly created
        return null;
    }

    switch (mappedPageObject) {
        case BENCHMARK_PAGE:
        case DUAL_LANGUAGE_PAGE:
        case STUDENT_SUMMARY_BENCHMARK_HISTORY_PAGE:
        case STUDENT_SUMMARY_TEXT_READING_HISTORY_PAGE:
        case STUDENT_SUMMARY_PROGRESS_PAGE:
        case PROGRESS_PAGE:
        case HOME_CONNECT_PAGE:
            return LRS_XAPI_LOCATION_OBJECT_IDS[mappedPageObject];
        case INSTRUCTIONS_PAGE: {
            const baseXapiObjectId = LRS_XAPI_LOCATION_OBJECT_IDS[mappedPageObject];

            if (!withoutTabName) {
                if (get(queryParams, INSTRUCTION, GROUPS) === GROUPS) {
                    return `${baseXapiObjectId}-groups`;
                }

                if (get(queryParams, INSTRUCTION) === STUDENTS) {
                    return `${baseXapiObjectId}-students`;
                }

                if (get(queryParams, INSTRUCTION) === ALL_ACTIVITIES) {
                    return `${baseXapiObjectId}-activities`;
                }
            }
            return baseXapiObjectId;
        }
        default:
            return null;
    }
};

export const getLrsObjectForChangeLocation = (payload, store, withoutTabName) => {
    const state = store.getState();
    const currentLrsObject = getLRSObjectForLocation(payload.location, withoutTabName);
    const previousLrsObject = getLRSObjectForLocation(state.router.location, withoutTabName);
    return currentLrsObject === previousLrsObject ? null : currentLrsObject;
};

const getLrsObjectForPdfGeneration = payload => {
    switch (payload?.documentKey) {
        case HOME_CONNECT_PDF: {
            const xapiLocationObjectId = getLRSObjectForLocation(window.location);

            return `${xapiLocationObjectId}-home-connect-report`;
        }
        case ALL_STUDENT_SUMMARY_PDF:
        case ALL_PM_GRAPHS_PDF:
        case CLASS_SUMMARY_PDF:
        case TRO_CLASS_SUMMARY_PDF:
        case CURRENT_PAGE:
            return EVENT_PRINT_ITEM;
        default:
            return null;
    }
};

export const getLrsProbeLocationFoProbeDetails = (currentSubtab = '') => {
    const { pathname } = window.location;
    const splitUrl = pathname.split('/');
    const currentProbeDetailsLocation = splitUrl[splitUrl.length - 1];

    const probeLocations = {
        benchmark: 'benchmark-report',
        benchmark_history: 'student-summary-report-benchmark-history',
        progress: `${convertToLowerKebabCase(currentSubtab)}-report`,
    };

    return probeLocations[currentProbeDetailsLocation];
};

const removeLineBreaks = (measure = '') => measure.replace(/\n/g, '');

const getLRSObjectForZoom = (allMeasures, measure) => {
    const measureTitle = removeLineBreaks(getMeasureTitleByKey(allMeasures, measure));
    return measureTitle ? `${measureTitle} PM Graph zoom` : null;
};

const getLRSObjectForPD = (allMeasures, measure) => {
    return removeLineBreaks(getDetailedMeasureName(allMeasures, measure));
};

export const getLrsObjectId = (action, store) => {
    let xapiLocationObjectId;
    let fullObjectId;

    switch (action.type) {
        case LOCATION_CHANGE: {
            xapiLocationObjectId = getLrsObjectForChangeLocation(action.payload, store, true);

            return xapiLocationObjectId;
        }
        case SELECT_INDIVIDUAL_STUDENT:
        case DOWNLOAD_STUDENT_ACTIVITY:
        case SORT_STUDENTS_BY_NAME:
        case SWITCH_PERIOD:
        case SWITCH_INSTRUCTION_DATE:
        case SEE_ACTIVITIES:
        case DOWNLOAD_ALL_ACTIVITIES:
        case OBSERVATION_SELECT:
        case HELP_DETAILS:
        case SELECT_PAGE:
        case SELECT_SKILL:
        case VIEW_LATEST_UPDATE_RECOMMENDATION:
        case DOWNLOAD_ACTIVITY: {
            const state = store.getState();
            xapiLocationObjectId = getLRSObjectForLocation(state.router.location);
            fullObjectId = `${xapiLocationObjectId}-${LRS_EVENTS_XAPI_IDS[action.type]}`;

            return fullObjectId;
        }
        case SWITCH_INSTRUCTION_TAB:
            return `${
                LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.INSTRUCTIONS_PAGE]
            }-${convertToLowerKebabCase(action?.tabName)}`;
        case PROBE_DETAIL_VISITING_EVENT: {
            return `${LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.BENCHMARK_PAGE]}-probe`;
        }
        case PRINT_CURRENT_PAGE:
            return getLrsObjectForPdfGeneration(action.payload);
        case GENERATE_PDF:
            return getLrsObjectForPdfGeneration(action.additionalResponseParams);
        case PM_GRAPH_ZOOM_EVENT:
            return `${LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.BENCHMARK_PAGE]}-progress-graph-zoom`;
        case PM_GRAPH_RESET_ZOOM_EVENT:
            return `${LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.BENCHMARK_PAGE]}-progress-graph-reset`;
        case PERCENTILE_EVENT:
            return `${LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.BENCHMARK_PAGE]}-percentile-button`;
        case SELECT_GRADE_YEAR:
            return `${LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.BENCHMARK_PAGE]}-${
                action.tabName
            }-grade-year`;
        case SELECT_HELP_ITEM:
            return EVENT_HELP_ITEM;
        case DOWNLOAD_PDF_LANGUAGE_CONSIDERATION:
            return EVENT_DOWNLOAD_PDF_LANGUAGE_CONSIDERATION;
        case SWITCH_PROGRESS_TAB:
            return `${
                LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.PROGRESS_PAGE]
            }-${convertToLowerKebabCase(action?.selectedTab)}`;
        case SWITCH_LANGUAGE_CONSIDERATIONS_TAB:
            return `${
                LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.INSTRUCTIONS_PAGE]
            }-groups-language-consideration`;
        case SELECTED_HEADER_DROPDOWN:
            return `https://api.amplify.com/xapi/objects/header-${convertToLowerKebabCase(
                action?.payload.dropdownName,
            )}`;
        case SWITCH_PM_PERIOD:
        case SWITCH_GROWTH_ANALYSIS_PERIOD:
            return `${
                LRS_XAPI_LOCATION_OBJECT_IDS[LRS_PAGE.PROGRESS_PAGE]
            }-${convertToLowerKebabCase(action?.subtabTitle)}-time-of-year`;
        case PROGRESS_GOAL_SET_EVENT:
            return 'https://api.amplify.com/xapi/objects/progress-dashboard-goal-setting';
        default:
            return null;
    }
};

export const getVerb = action => {
    const { type } = action;
    switch (type) {
        case LOCATION_CHANGE: {
            const { location } = action.payload;
            const appRootUrl = `/${location.pathname.split('/')[1]}`;
            const mappedPageObject = LrsPageMapper(appRootUrl)[location.pathname];
            return STUDENT_SUMMARY_TABS.includes(mappedPageObject) ? SELECTED : VIEWED;
        }
        case SWITCH_INSTRUCTION_TAB:
        case SWITCH_PROGRESS_TAB:
            return VIEWED;
        case SWITCH_PERIOD:
        case SELECT_INDIVIDUAL_STUDENT:
        case SORT_STUDENTS_BY_NAME:
        case SELECT_STUDENT:
        case PROBE_DETAIL_VISITING_EVENT:
        case SELECT_SKILL:
        case SELECT_PAGE:
        case HELP_DETAILS:
        case SELECT_HELP_ITEM:
        case SEE_ACTIVITIES:
        case VIEW_LATEST_UPDATE_RECOMMENDATION:
        case SWITCH_INSTRUCTION_DATE:
        case SWITCH_LANGUAGE_CONSIDERATIONS_TAB:
        case SELECT_GRADE_YEAR:
        case SELECTED_HEADER_DROPDOWN:
        case SWITCH_PM_PERIOD:
        case SWITCH_GROWTH_ANALYSIS_PERIOD:
            return SELECTED;
        case DOWNLOAD_STUDENT_ACTIVITY:
        case DOWNLOAD_ACTIVITY:
        case DOWNLOAD_ALL_ACTIVITIES:
        case PRINT_CURRENT_PAGE:
        case GENERATE_PDF:
        case DOWNLOAD_PDF_LANGUAGE_CONSIDERATION:
            return DOWNLOADED;
        case PM_GRAPH_ZOOM_EVENT:
        case PM_GRAPH_RESET_ZOOM_EVENT:
        case PERCENTILE_EVENT:
        case OBSERVATION_SELECT:
        case LEXILE_EVENT:
            return PRESSED;
        case PROGRESS_GOAL_SET_EVENT:
            return DEFINED;
        default:
            return ACCESSED;
    }
};

export const getAppName = () => {
    const appNames = {
        reading: 'mclass-english',
        espanol: 'mclass-lectura',
    };
    const url = window.location.pathname;
    const app = url.split('/')[1]?.split('-');

    return appNames[app[0]];
};

export const getLrsEventData = (staffId, lrsTrackingInfo) => ({
    userId: staffId,
    resultExtension: {
        ...Object.values(LRS_RESULT_EXTENSIONS).reduce((extensions, extension) => {
            return { ...extensions, [extension]: lrsTrackingInfo[extension] };
        }, {}),
    },
    objectExtension: {
        ...omit(lrsTrackingInfo, Object.values(LRS_RESULT_EXTENSIONS)),
    },
});

const getShownStatus = value => (value ? 'on' : 'off');

const getCollapsedStatus = value => (value ? 'collapsed' : 'all');

const getValue = value => value || undefined;

const getLocationExtensions = (mappedPageObject, studentId) => {
    const extensions = {};

    if (STUDENT_SUMMARY_TABS.includes(mappedPageObject)) {
        extensions[ETERNAL_STUDENT_ID] = studentId;
    }
    return extensions;
};

const getPDFExtensions = (pdfInHeader, documentKey, selectedStudentsData = []) => {
    const extensions = {};
    if (!pdfInHeader) {
        const { search } = window.location;
        const urlParams = new URLSearchParams(search);
        const period = urlParams.get('period');
        const englishIds = [];
        const spanishIds = [];

        selectedStudentsData.forEach(({ id, isEnglishChecked, isSpanishChecked }) => {
            if (isEnglishChecked) {
                englishIds.push(id);
            }
            if (isSpanishChecked) {
                spanishIds.push(id);
            }
        });

        extensions[STUDENT_ENGLISH_ID] = englishIds;
        extensions[STUDENT_SPANISH_ID] = spanishIds;
        extensions[TIME_OF_YEAR] = convertToLowerKebabCase(period);
    } else {
        extensions[PRINT_ITEM] = convertToLowerKebabCase(documentKey);
    }

    return extensions;
};

export const getLrsExtensions = (action, allMeasures) => {
    const { type } = action;
    let extensions = {};

    const optionalGroupExtension = value => {
        if (value) {
            extensions[GROUP_ID] = value;
        }
    };

    switch (type) {
        case SWITCH_PERIOD: {
            const { studentId, selectedPeriod } = action;
            extensions[TIME_OF_YEAR] = selectedPeriod?.toLowerCase();
            extensions[ETERNAL_STUDENT_ID] = getValue(studentId);
            break;
        }

        case SWITCH_PROGRESS_TAB: {
            extensions[TIME_FRAME] = convertToLowerKebabCase(action?.selectedPeriod);
            break;
        }

        case PROGRESS_GOAL_SET_EVENT: {
            const { selectedPeriod, measure, goalScore, prevGoalScore, growthRatePrev } = action;
            const { prevGoalRange, prevGoalLevel } = growthRatePrev;

            extensions[TIME_FRAME] = convertToLowerKebabCase(selectedPeriod);

            extensions[SKILL] = measure.toLowerCase();
            extensions[PREVIOUS_GOAL_RANGE] = prevGoalRange;
            extensions[PREVIOUS_GOAL_LEVEL] = prevGoalLevel;
            extensions[GOAL_SCORE] = goalScore;
            extensions[PREVIOUS_GOAL_SCORE] = prevGoalScore;
            break;
        }

        case SORT_STUDENTS_BY_NAME: {
            const sortDir = action.sortDirection;
            extensions[NAME_SORT] = SORT_DIRECTIONS[sortDir];
            break;
        }

        case DOWNLOAD_STUDENT_ACTIVITY: {
            const activityValue = action.activityName;
            const instructionalId = action.instructionalActivityId;
            extensions[RECOMMENDED_ACTIVITY] = convertToLowerKebabCase(activityValue);
            extensions[INSTRUCTIONAL_ACTIVITY_ID] = `${instructionalId}`;
            extensions[ETERNAL_STUDENT_ID] = action.studentId;
            break;
        }

        case PROBE_DETAIL_VISITING_EVENT: {
            const { measure, studentId, currentSubtab } = action;

            extensions[PROBE_DETAILS] = getLRSObjectForPD(allMeasures, measure);
            extensions[PROBE_LOCATION] = getLrsProbeLocationFoProbeDetails(currentSubtab);
            extensions[ETERNAL_STUDENT_ID] = studentId;
            break;
        }

        case PM_GRAPH_ZOOM_EVENT:
        case PM_GRAPH_RESET_ZOOM_EVENT: {
            extensions[ZOOM_DETAILS] = getLRSObjectForZoom(allMeasures, action.measure);
            extensions[ETERNAL_STUDENT_ID] = action.studentId;
            break;
        }

        case PERCENTILE_EVENT: {
            extensions[BUTTON_STATUS] = getShownStatus(action.value);
            break;
        }

        case SWITCH_INSTRUCTION_DATE: {
            extensions[RECOMMENDATION] = 'past';
            extensions[RECOMMENDATION_TIME] = action.payload;
            extensions[PAST_RECOMMENDATION_TIME] = action.prevDate;
            break;
        }

        case VIEW_LATEST_UPDATE_RECOMMENDATION: {
            const { date, buttonText } = action.payload;
            extensions[RECOMMENDATION_TIME] = date;
            extensions[BUTTON_TEXT] = buttonText;
            break;
        }
        case SELECT_INDIVIDUAL_STUDENT: {
            const { studentId, group } = action;
            extensions[ETERNAL_STUDENT_ID] = studentId;
            optionalGroupExtension(group);
            break;
        }

        case SEE_ACTIVITIES: {
            const { collapsed, group } = action;
            extensions[PREVIOUS_SEE_ACTIVITIES] = getCollapsedStatus(collapsed);
            extensions[SEE_ACTIVITIES_EXT] = getCollapsedStatus(!collapsed);
            optionalGroupExtension(group);
            break;
        }

        case HELP_DETAILS: {
            const { name, category, assessment, studentId, group } = action;
            extensions[PATTERN_CATEGORY] = convertToLowerKebabCase(category);
            extensions[PATTERN_OBSERVED] = name;
            extensions[PATTERN_ASSESSMENT] = convertToLowerKebabCase(assessment);
            extensions[ETERNAL_STUDENT_ID] = studentId;
            optionalGroupExtension(group);
            break;
        }

        case OBSERVATION_SELECT: {
            const { name, category, assessment, checked, studentId, group } = action;
            extensions[PATTERN_CATEGORY] = convertToLowerKebabCase(category);
            extensions[PATTERN_OBSERVED] = name;
            extensions[PATTERN_ASSESSMENT] = convertToLowerKebabCase(assessment);
            extensions[PATTERN_ENABLED] = checked;
            extensions[ETERNAL_STUDENT_ID] = getValue(studentId);
            optionalGroupExtension(group);
            break;
        }

        case DOWNLOAD_ALL_ACTIVITIES: {
            const { location, activities, group } = action;
            extensions[DOWNLOAD_LOCATION] = location;
            extensions[INSTRUCTIONAL_ACTIVITY_ID] = activities;
            optionalGroupExtension(group);
            break;
        }

        case SELECT_PAGE: {
            const { item, activityIds, pagesList, group } = action;
            extensions[PAGINATION_SELECTED] = item;
            extensions[INSTRUCTIONAL_ACTIVITY_ID] = activityIds;
            extensions[PAGINATION_SHOWN] = pagesList;
            optionalGroupExtension(group);
            break;
        }

        case SELECT_SKILL:
        case DOWNLOAD_ACTIVITY: {
            const { skill, grade, instructionalActivityId } = action;
            extensions[PARENT_GRADE] = grade;
            extensions[PARENT_SKILL] = convertToLowerKebabCase(skill);
            extensions[INSTRUCTIONAL_ACTIVITY_ID] = getValue(instructionalActivityId);
            break;
        }

        case PRINT_CURRENT_PAGE: {
            const { pdfInHeader, documentKey } = action.payload;

            extensions = { ...getPDFExtensions(pdfInHeader, documentKey) };

            break;
        }

        case GENERATE_PDF: {
            const { selectedStudentsData, pdfInHeader, documentKey } =
                action.additionalResponseParams;

            extensions = { ...getPDFExtensions(pdfInHeader, documentKey, selectedStudentsData) };

            break;
        }

        case SELECT_HELP_ITEM: {
            const { title } = action.payload;

            extensions[HELP_ITEM] = convertToLowerKebabCase(title);
            break;
        }

        case DOWNLOAD_PDF_LANGUAGE_CONSIDERATION: {
            const { activityId, title, group } = action.payload;
            extensions[DOWNLOAD_LOCATION] = convertToLowerKebabCase(title);
            extensions[INSTRUCTIONAL_ACTIVITY_ID] = activityId;
            optionalGroupExtension(group);
            break;
        }

        case LOCATION_CHANGE: {
            const { location } = action.payload;
            const { search } = location;
            const urlParams = new URLSearchParams(search);
            const studentId = urlParams.get('studentId');
            const appRootUrl = `/${location.pathname.split('/')[1]}`;
            const mappedPageObject = LrsPageMapper(appRootUrl)[location.pathname];

            extensions = { ...getLocationExtensions(mappedPageObject, studentId) };
            break;
        }

        case LEXILE_EVENT: {
            extensions[BUTTON_STATUS] = getShownStatus(action?.isLexileShown);
            break;
        }

        case SWITCH_INSTRUCTION_TAB: {
            if (action.tabName === STUDENTS) {
                optionalGroupExtension(action.group);
            }
            break;
        }

        case SWITCH_LANGUAGE_CONSIDERATIONS_TAB: {
            const { tabName, group } = action;
            extensions[LANGUAGE_CONSIDERATIONS] = convertToLowerKebabCase(tabName);
            optionalGroupExtension(group);
            break;
        }

        case SELECTED_HEADER_DROPDOWN: {
            const { classe, district, school, nameShown, nameList, nameSelected } = action.payload;

            extensions[DISTRICT_ID] = district;
            extensions[SCHOOL_ID] = school;
            extensions[CLASS_ID] = classe;

            extensions[NAME_SHOWN] = nameShown;
            extensions[NAME_LIST] = nameList.join(', ');
            extensions[NAME_SELECTED] = nameSelected;
            break;
        }

        case SWITCH_PM_PERIOD:
        case SWITCH_GROWTH_ANALYSIS_PERIOD: {
            const { selectedPeriod, selectedGrowthAnalysisPeriod } = action;
            const period = selectedPeriod || selectedGrowthAnalysisPeriod;

            extensions[TIME_OF_YEAR] = convertToLowerKebabCase(period);
            break;
        }

        case SELECT_GRADE_YEAR: {
            const { selectedGradeYear, studentId } = action;
            const { grade, year } = parseGradeYearPair(selectedGradeYear);

            extensions[ETERNAL_STUDENT_ID] = studentId;
            extensions[YEAR] = `${year - 1}-${year}`;
            extensions[GRADE] = grade;
            break;
        }

        default:
            return extensions;
    }

    return extensions;
};

export const lrsSelectedHeaderDropdownProps = {
    data: {},
    setData(value) {
        this.data = value;
    },
};

// needed for xApi group-id extention values
export const formLrsGroupName = (name, groupNumber = 1) =>
    name ? `Group ${groupNumber} - ${name}` : '';

export const findFormLrsGroupNumber = (arr, name) => arr.findIndex(item => item.name === name) + 1;

export const setLrsSelectedHeaderDropdownProps = (
    chosenItemId,
    selectedDropdownProps,
    dropdownName,
) => {
    if (isEmpty(selectedDropdownProps) || !chosenItemId || !dropdownName) {
        lrsSelectedHeaderDropdownProps.setData({});
        return;
    }

    lrsSelectedHeaderDropdownProps.setData({
        dropdownName,
        nameShown: selectedDropdownProps?.chosenItem?.name,
        nameList: selectedDropdownProps?.items.map(item => item.name),
        nameSelected: selectedDropdownProps?.items.find(item => item.uid === chosenItemId).name,
    });
};
